import fragmentShaderSource from "./shaders/benchmark.frag";
import vertexShaderSource from "./shaders/benchmark.vert";
import type { EXT_disjoint_timer_query_webgl2 } from "./types";

export class GPUBenchmark {
  private _gl: WebGL2RenderingContext;
  private _program: WebGLProgram;
  private _subdivUniformLocation: WebGLUniformLocation;
  private _resolutionUniformLocation: WebGLUniformLocation;
  private _colorTextureUniformLocation: WebGLUniformLocation;
  private _noiseTextureUniformLocation: WebGLUniformLocation;
  private _timerExt: EXT_disjoint_timer_query_webgl2 | null;
  private _canvasSize: number;
  private _debug: boolean;
  private _canvas: HTMLCanvasElement;
  private _colorTexture: WebGLTexture;
  private _noiseTexture: WebGLTexture;

  constructor(canvasSize: number = 512, debug: boolean = false) {
    this._canvasSize = canvasSize;
    this._debug = debug;

    // Create offscreen canvas
    const canvas = document.createElement("canvas");
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    this._canvas = canvas;

    // Get WebGL2 context
    const gl = canvas.getContext("webgl2", {
      antialias: false,
      depth: false,
      stencil: false,
      alpha: false,
      preserveDrawingBuffer: true,
      powerPreference: "high-performance",
    });

    if (!gl) {
      throw new Error("WebGL2 not supported");
    }

    // Log WebGL context info
    if (this._debug) {
      console.log("WebGL2 context created");
      console.log("Vendor:", gl.getParameter(gl.VENDOR));
      console.log("Renderer:", gl.getParameter(gl.RENDERER));
      console.log("WebGL Version:", gl.getParameter(gl.VERSION));
      console.log("GLSL Version:", gl.getParameter(gl.SHADING_LANGUAGE_VERSION));
    }

    this._gl = gl;

    // Try to get timer extension
    this._timerExt = null; // gl.getExtension("EXT_disjoint_timer_query_webgl2");

    // Initialize shaders
    this._program = this._createShaderProgram();

    // Get uniform locations
    const subdivLoc = gl.getUniformLocation(this._program, "uSubdiv");
    const resLoc = gl.getUniformLocation(this._program, "uResolution");
    const colorTexLoc = gl.getUniformLocation(this._program, "uColorTexture");
    const noiseTexLoc = gl.getUniformLocation(this._program, "uNoiseTexture");

    if (!subdivLoc || !resLoc || !colorTexLoc || !noiseTexLoc) {
      throw new Error("Failed to get uniform locations");
    }

    this._subdivUniformLocation = subdivLoc;
    this._resolutionUniformLocation = resLoc;
    this._colorTextureUniformLocation = colorTexLoc;
    this._noiseTextureUniformLocation = noiseTexLoc;

    // Create textures
    this._colorTexture = this._createColorTexture();
    this._noiseTexture = this._createNoiseTexture();

    // Setup viewport
    gl.viewport(0, 0, canvasSize, canvasSize);

    // Test basic rendering
    if (this._debug) {
      this._testBasicRendering();
    }

    // Warm up the shader to avoid compilation delays during benchmark
    this._warmupShader();
  }

  private _warmupShader(): void {
    const gl = this._gl;

    // Use the benchmark shader
    gl.useProgram(this._program);

    // Set minimal uniforms
    gl.uniform1i(this._subdivUniformLocation, 8);
    gl.uniform2f(this._resolutionUniformLocation, 64, 64);

    // Clear and draw once to trigger shader compilation
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Don't sync, just flush to ensure shader is compiled
    gl.flush();
  }

  private _testBasicRendering(): void {
    const gl = this._gl;

    // Clear to red to test basic operations
    gl.clearColor(1, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const pixels = new Uint8Array(4);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    if (pixels[0] !== 255 || pixels[1] !== 0 || pixels[2] !== 0) {
      console.error("Basic rendering test failed! Clear color not working.");
    } else {
      console.log("Basic rendering test passed");
    }
  }

  private _createShaderProgram(): WebGLProgram {
    const gl = this._gl;

    // Create shaders
    const vertexShader = this._createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = this._createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

    // Create program
    const program = gl.createProgram();
    if (!program) {
      throw new Error("Failed to create shader program");
    }

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program);
      throw new Error(`Failed to link shader program: ${info}`);
    }

    if (this._debug) {
      console.log("Shader program linked successfully");
    }

    // Clean up shaders (they're linked to the program now)
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    return program;
  }

  private _createShader(type: number, source: string): WebGLShader {
    const gl = this._gl;
    const shader = gl.createShader(type);

    if (!shader) {
      throw new Error("Failed to create shader");
    }

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const info = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      const shaderType = type === gl.VERTEX_SHADER ? "vertex" : "fragment";
      console.error(`Failed to compile ${shaderType} shader:`, info);
      throw new Error(`Failed to compile ${shaderType} shader: ${info}`);
    }

    return shader;
  }

  private _createColorTexture(): WebGLTexture {
    const gl = this._gl;
    const size = 256;
    const data = new Uint8Array(size * size * 4);

    // Generate random colors
    for (let i = 0; i < size * size * 4; i += 4) {
      // Random bright colors
      data[i] = Math.floor(Math.random() * 192) + 64; // R: 127-255
      data[i + 1] = Math.floor(Math.random() * 192) + 64; // G: 127-255
      data[i + 2] = Math.floor(Math.random() * 192) + 64; // B: 127-255
      data[i + 3] = 255; // A: 255
    }

    const texture = gl.createTexture();
    if (!texture) {
      throw new Error("Failed to create color texture");
    }

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);

    return texture;
  }

  private _createNoiseTexture(): WebGLTexture {
    const gl = this._gl;
    const size = 256;
    const data = new Uint8Array(size * size);

    // Simple Perlin-like noise using multiple octaves
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        let value = 0;
        let amplitude = 1;
        let frequency = 0.01;

        // Add multiple octaves
        for (let octave = 0; octave < 4; octave++) {
          const nx = x * frequency;
          const ny = y * frequency;

          // Simple pseudo-random based on sin/cos
          const noise = (Math.sin(nx * 12.9898 + ny * 78.233) * 43758.5453) % 1;
          value += noise * amplitude;

          amplitude *= 0.5;
          frequency *= 2;
        }

        // Normalize to 0-255
        data[y * size + x] = Math.floor((value * 0.5 + 0.5) * 255);
      }
    }

    const texture = gl.createTexture();
    if (!texture) {
      throw new Error("Failed to create noise texture");
    }

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, size, size, 0, gl.RED, gl.UNSIGNED_BYTE, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);

    return texture;
  }

  async renderFrame(subdiv: number, framesPerTest: number = 1): Promise<number> {
    const gl = this._gl;

    if (this._debug) {
      console.log(`Rendering ${framesPerTest} frames with SUBDIV=${subdiv}`);
    }

    // Use shader program
    gl.useProgram(this._program);

    // Set uniforms
    gl.uniform1i(this._subdivUniformLocation, subdiv);
    gl.uniform2f(this._resolutionUniformLocation, this._canvasSize, this._canvasSize);

    // Bind textures
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this._colorTexture);
    gl.uniform1i(this._colorTextureUniformLocation, 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this._noiseTexture);
    gl.uniform1i(this._noiseTextureUniformLocation, 1);

    if (this._debug) {
      // Verify uniforms were set
      const currentProgram = gl.getParameter(gl.CURRENT_PROGRAM);
      console.log("Current program:", currentProgram === this._program);
      const subdivValue = gl.getUniform(this._program, this._subdivUniformLocation);
      console.log("SUBDIV uniform value:", subdivValue);
    }

    // Clear before rendering frames
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const startTime: number = performance.now();
    let totalGpuTime = 0;
    let gpuTimerUsed = false;

    // Render multiple frames
    for (let i = 0; i < framesPerTest; i++) {
      let query: WebGLQuery | null = null;

      // Try to use GPU timer if available
      if (this._timerExt) {
        query = gl.createQuery();
        if (query) {
          gl.beginQuery(this._timerExt.TIME_ELAPSED_EXT, query);
        }
      }

      // Clear each frame to ensure work is done
      if (i > 0) {
        gl.clear(gl.COLOR_BUFFER_BIT);
      }

      // Draw full screen quad (6 vertices for 2 triangles)
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      // Check for errors after each draw
      const drawError = gl.getError();
      if (drawError !== gl.NO_ERROR) {
        console.error(`WebGL error during drawArrays: ${drawError}`);
      }

      // Get GPU timing if available
      if (this._timerExt && query) {
        gl.endQuery(this._timerExt.TIME_ELAPSED_EXT);

        // Wait for query result
        await this._waitForQueryResult(query);

        // Check if result is valid (no disjoint operation)
        const disjoint = gl.getParameter(this._timerExt.GPU_DISJOINT_EXT);
        if (!disjoint) {
          const gpuTime = gl.getQueryParameter(query, gl.QUERY_RESULT);
          totalGpuTime += gpuTime / 1000000; // Convert nanoseconds to milliseconds
          gpuTimerUsed = true;
        }

        gl.deleteQuery(query);
      }
    }

    // Use fence sync to ensure GPU completion on all platforms
    const sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
    if (!sync) {
      console.warn("fenceSync not supported, falling back to gl.finish()");
      gl.finish();
    } else {
      gl.flush(); // Ensure commands are pushed to the driver

      let status: GLenum;
      const startWait = performance.now();
      const maxWaitTime = 1000; // 1 second max wait

      // Get the maximum allowed timeout value
      const maxTimeout = gl.getParameter(gl.MAX_CLIENT_WAIT_TIMEOUT_WEBGL);

      if (this._debug) {
        console.log(
          `MAX_CLIENT_WAIT_TIMEOUT_WEBGL: ${maxTimeout} nanoseconds (${(maxTimeout / 1_000_000).toFixed(2)}ms)`
        );
      }

      do {
        // Wait up to the maximum allowed time (typically 0-1ms)
        status = gl.clientWaitSync(sync, 0, maxTimeout);

        // Prevent infinite loop
        if (performance.now() - startWait > maxWaitTime) {
          console.warn("GPU sync timeout after 1 second");
          break;
        }

        // Yield to prevent blocking if we're in a tight loop
        if (status === gl.TIMEOUT_EXPIRED) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      } while (status === gl.TIMEOUT_EXPIRED);

      gl.deleteSync(sync);

      if (this._debug && status === gl.WAIT_FAILED) {
        console.error("GPU sync wait failed");
      }
    }

    const endTime = performance.now();
    const totalCpuTime = endTime - startTime;

    // Verify rendering actually happened by checking a pixel
    if (this._debug || framesPerTest === 1) {
      const pixels = new Uint8Array(4);
      gl.readPixels(this._canvasSize / 2, this._canvasSize / 2, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
      if (this._debug) {
        console.log(`Center pixel RGBA: [${pixels[0]}, ${pixels[1]}, ${pixels[2]}, ${pixels[3]}]`);
      }

      // Check if we got any non-black pixels
      if (pixels[0] === 0 && pixels[1] === 0 && pixels[2] === 0) {
        console.warn("Warning: Rendered frame appears to be black. Shader may not be executing properly.");
      }
    }

    // Check for WebGL errors
    const error = gl.getError();
    if (error !== gl.NO_ERROR) {
      console.error(`WebGL error after rendering: ${error}`);
    }

    // Debug timing info
    if (this._debug) {
      if (gpuTimerUsed) {
        console.log(
          `GPU time: ${totalGpuTime.toFixed(3)}ms total, ${(totalGpuTime / framesPerTest).toFixed(3)}ms per frame`
        );
        console.log(
          `CPU time: ${totalCpuTime.toFixed(3)}ms total, ${(totalCpuTime / framesPerTest).toFixed(3)}ms per frame`
        );
      } else {
        console.log(
          `CPU time (no GPU timer): ${totalCpuTime.toFixed(3)}ms total, ${(totalCpuTime / framesPerTest).toFixed(3)}ms per frame`
        );
      }
    }

    // Return average frame time
    if (gpuTimerUsed) {
      return totalGpuTime / framesPerTest;
    }
    // CPU timing fallback - divide total time by number of frames
    return totalCpuTime / framesPerTest;
  }

  private async _waitForQueryResult(query: WebGLQuery): Promise<void> {
    const gl = this._gl;

    // Poll for result availability
    let attempts = 0;
    while (attempts < 1000) {
      // Prevent infinite loop
      const available = gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE);
      if (available) {
        break;
      }
      // Wait a bit before checking again
      await new Promise((resolve) => setTimeout(resolve, 1));
      attempts++;
    }
  }

  hasGPUTimer(): boolean {
    return !!this._timerExt;
  }

  captureFrame(): ImageData {
    const gl = this._gl;
    const width = this._canvasSize;
    const height = this._canvasSize;

    // Read pixels from the framebuffer
    const pixels = new Uint8ClampedArray(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    // Flip vertically (WebGL renders upside down)
    const flippedPixels = new Uint8ClampedArray(width * height * 4);
    for (let y = 0; y < height; y++) {
      const srcRow = y * width * 4;
      const dstRow = (height - y - 1) * width * 4;
      flippedPixels.set(pixels.subarray(srcRow, srcRow + width * 4), dstRow);
    }

    return new ImageData(flippedPixels, width, height);
  }

  dispose(): void {
    const gl = this._gl;

    // Delete WebGL resources
    gl.deleteProgram(this._program);
    gl.deleteTexture(this._colorTexture);
    gl.deleteTexture(this._noiseTexture);

    // Lose the WebGL context to free GPU resources
    const loseContext = gl.getExtension("WEBGL_lose_context");
    if (loseContext) {
      loseContext.loseContext();
    }

    // Clear references
    this._canvas.width = 0;
    this._canvas.height = 0;

    // Canvas will be garbage collected since it was never added to DOM
  }
}
